import { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Sofa, Refrigerator, Trees, HardHat, Bath, Home, Trash2,
  Phone, ArrowUpRight, Menu, X, Star, Truck, Recycle, Clock,
} from "lucide-react";
import QuoteDialog from "@/components/QuoteDialog";
import BookingSection from "@/components/BookingSection";
import ContactSection from "@/components/ContactSection";
import {
  PHONE, PHONE_HREF, SERVICES, SERVICE_AREAS, STEPS, TESTIMONIALS, IMAGES,
} from "@/lib/site";

const ICONS = { Sofa, Refrigerator, Trees, HardHat, Bath, Home, Trash2 };

const ease = [0.16, 1, 0.3, 1];

const HeroLine = ({ children, delay }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.div>
  </div>
);

const NAV = [
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how" },
  { label: "Service Area", href: "#area" },
  { label: "Book Online", href: "#booking" },
  { label: "Contact", href: "#contact" },
];

export default function Landing() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 200]);
  const heroScale = useTransform(scrollY, [0, 700], [1, 1.15]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative bg-[#050505] text-white">
      {/* NAV */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-[#050505]/90 backdrop-blur-md border-b border-[#222]" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] flex items-center justify-between px-4 md:px-12 h-16 md:h-20">
          <button onClick={() => go("#top")} data-testid="nav-logo" className="flex items-center gap-2 text-left">
            <span className="flex h-9 w-9 items-center justify-center bg-[#FF4D00]">
              <Truck className="h-5 w-5 text-black" />
            </span>
            <span className="font-display text-lg md:text-xl uppercase leading-none tracking-tight">
              Mile High<br className="hidden sm:block" /> Junk Guys
            </span>
          </button>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <button
                key={n.href} onClick={() => go(n.href)}
                data-testid={`nav-${n.href.slice(1)}`}
                className="font-mono-a text-xs tracking-widest uppercase text-[#aaa] hover:text-[#FF4D00] transition-colors"
              >
                {n.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a href={PHONE_HREF} data-testid="nav-call" className="hidden sm:flex items-center gap-2 font-mono-a text-xs md:text-sm text-white hover:text-[#FF4D00] transition-colors">
              <Phone className="h-4 w-4 text-[#FF4D00]" /> {PHONE}
            </a>
            <button
              onClick={() => setQuoteOpen(true)} data-testid="nav-quote-btn"
              className="hidden md:inline-flex h-10 items-center px-5 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a text-xs uppercase tracking-widest transition-colors"
            >
              Free Quote
            </button>
            <button onClick={() => setMenuOpen((v) => !v)} data-testid="nav-menu-toggle" className="lg:hidden p-2">
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-[#222] bg-[#050505] px-4 py-4 space-y-1">
            {NAV.map((n) => (
              <button key={n.href} onClick={() => go(n.href)} className="block w-full text-left py-3 font-mono-a text-sm uppercase tracking-widest text-[#ccc] border-b border-[#1a1a1a]">
                {n.label}
              </button>
            ))}
            <button onClick={() => { setMenuOpen(false); setQuoteOpen(true); }} className="mt-3 w-full h-12 bg-[#FF4D00] text-black font-mono-a text-sm uppercase tracking-widest">
              Free Quote
            </button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 z-0">
          <img src={IMAGES.hero} alt="Junk removal crew loading a truck" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/40" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-4 md:px-12 pb-16 md:pb-24 pt-32">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono-a text-xs md:text-sm tracking-[0.25em] uppercase text-[#FF4D00] mb-5"
          >
            / Family-Owned · Denver, CO
          </motion.p>
          <h1 className="font-display font-bold uppercase leading-[0.85] tracking-tighter text-6xl sm:text-7xl lg:text-[8.5rem]">
            <HeroLine delay={0.35}>Your Junk.</HeroLine>
            <HeroLine delay={0.5}><span className="text-[#FF4D00]">Gone Today.</span></HeroLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }}
            className="mt-8 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-12"
          >
            <p className="max-w-md text-[#bbb] text-base md:text-lg">
              Two Guys, one truck, zero hassle. Same-day junk removal across Denver, Thornton, Northglenn, Arvada & Broomfield — with upfront pricing you'll actually like.
            </p>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                onClick={() => setQuoteOpen(true)} data-testid="hero-quote-btn"
                className="group inline-flex h-14 items-center gap-2 px-8 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a text-sm uppercase tracking-widest transition-colors"
              >
                Get Free Quote
                <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
              <a
                href={PHONE_HREF} data-testid="hero-call-btn"
                className="inline-flex h-14 items-center gap-2 px-8 border border-white/30 hover:border-[#FF4D00] hover:text-[#FF4D00] font-mono-a text-sm uppercase tracking-widest transition-colors"
              >
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="relative z-10 border-y border-[#222] bg-[#FF4D00] py-4">
        <Marquee speed={45} gradient={false}>
          {["Furniture Removal", "Appliance Haul-Away", "Yard Debris", "Construction Debris", "Hot Tub Removal", "Estate Cleanouts", "Garage & Basement", "Same-Day Service"].map((t, i) => (
            <span key={i} className="mx-8 font-display text-2xl md:text-3xl uppercase text-black tracking-tight flex items-center gap-8">
              {t} <Star className="h-4 w-4 fill-black" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* STATS */}
      <section className="relative z-10 bg-[#050505] px-4 md:px-12 lg:px-24 py-14 border-b border-[#222]">
        <div className="mx-auto max-w-6xl grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Clock, n: "Same Day", l: "Availability" },
            { icon: Truck, n: "5,000+", l: "Loads Hauled" },
            { icon: Star, n: "5-Star", l: "Local Rated" },
            { icon: Recycle, n: "60%+", l: "Diverted / Donated" },
          ].map((s, i) => (
            <motion.div
              key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08, ease }}
            >
              <s.icon className="h-6 w-6 text-[#FF4D00] mb-3" />
              <p className="font-display text-3xl md:text-4xl uppercase leading-none">{s.n}</p>
              <p className="font-mono-a text-[11px] tracking-widest uppercase text-[#888] mt-2">{s.l}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="relative z-10 bg-[#050505] px-4 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="font-mono-a text-xs tracking-[0.2em] uppercase text-[#FF4D00]">/ What We Haul</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mt-4 leading-[0.9]">
                If It's Junk,<br />We Take It.
              </h2>
            </div>
            <p className="text-[#888] max-w-sm md:text-lg">
              From a single couch to a full estate, our crew handles the lifting, loading and cleanup. You just point.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              const big = i === 0;
              return (
                <motion.button
                  key={s.key}
                  onClick={() => setQuoteOpen(true)}
                  data-testid={`service-${s.key}`}
                  initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05, ease }}
                  className={`group text-left border border-[#222] bg-[#0d0d0d] p-6 md:p-8 hover:bg-[#141414] hover:border-[#FF4D00]/50 transition-colors relative overflow-hidden ${
                    big ? "md:col-span-6 md:row-span-2 min-h-[220px] md:min-h-[460px] flex flex-col justify-between" : "md:col-span-3"
                  }`}
                >
                  {big && (
                    <img src={IMAGES.loading} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" />
                  )}
                  <div className="relative z-10 flex items-center justify-between">
                    <Icon className="h-8 w-8 text-[#FF4D00]" />
                    <ArrowUpRight className="h-5 w-5 text-[#555] group-hover:text-[#FF4D00] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <div className="relative z-10 mt-6">
                    <h3 className={`font-display uppercase tracking-tight ${big ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"}`}>{s.title}</h3>
                    <p className="text-[#888] text-sm mt-2 max-w-xs">{s.desc}</p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* MANIFESTO / HOW IT WORKS */}
      <section id="how" className="relative z-10 bg-[#0a0a0a] border-y border-[#222] px-4 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="mx-auto max-w-[1400px]">
          <p className="font-mono-a text-xs tracking-[0.2em] uppercase text-[#FF4D00]">/ The Family Way</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mt-4 mb-14 leading-[0.9]">
            Simple. Honest.<br />Done In A Day.
          </h2>
          <div className="border-t border-[#222]">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.05, ease }}
                className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline border-b border-[#222] py-8 md:py-10 hover:bg-[#111] transition-colors px-2 md:px-4"
              >
                <span className="md:col-span-2 font-mono-a text-[#FF4D00] text-lg">{s.n}/</span>
                <h3 className="md:col-span-4 font-display text-2xl md:text-3xl uppercase tracking-tight group-hover:translate-x-2 transition-transform">{s.title}</h3>
                <p className="md:col-span-6 text-[#888] md:text-lg">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section id="area" className="relative z-10 bg-[#050505] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[320px] lg:min-h-[560px]">
            <img src={IMAGES.skyline} alt="Denver skyline and Rocky Mountains" className="absolute inset-0 h-full w-full object-cover img-grayscale" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505] lg:to-[#050505]" />
          </div>
          <div className="px-4 md:px-12 lg:px-16 py-16 lg:py-24 flex flex-col justify-center">
            <p className="font-mono-a text-xs tracking-[0.2em] uppercase text-[#FF4D00]">/ Where We Work</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-4 leading-[0.9]">
              Proudly Serving<br />The Front Range
            </h2>
            <p className="text-[#888] md:text-lg mt-5 max-w-md">
              Born and raised here. We know these neighborhoods because we live in them — and we treat your home like it's ours.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {SERVICE_AREAS.map((a, i) => (
                <motion.div
                  key={a} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06, ease }}
                  className="flex items-center gap-3 border border-[#222] bg-[#0d0d0d] px-4 h-14"
                >
                  <span className="h-2 w-2 bg-[#FF4D00]" />
                  <span className="font-display text-lg md:text-xl uppercase tracking-tight">{a}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative z-10 bg-[#0a0a0a] border-y border-[#222] px-4 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="mx-auto max-w-[1400px]">
          <p className="font-mono-a text-xs tracking-[0.2em] uppercase text-[#FF4D00] mb-10">/ From The Neighbors</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08, ease }}
                className="border border-[#222] bg-[#0d0d0d] p-6 md:p-8 flex flex-col"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-[#FF4D00] text-[#FF4D00]" />)}
                </div>
                <blockquote className="text-white/90 md:text-lg leading-relaxed flex-1">"{t.quote}"</blockquote>
                <figcaption className="mt-6 pt-4 border-t border-[#222]">
                  <p className="font-display text-lg uppercase tracking-tight">{t.name}</p>
                  <p className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">{t.area}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative z-10 bg-[#FF4D00] px-4 md:px-12 lg:px-24 py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-black leading-[0.9]">
            Ready To Clear<br />The Clutter?
          </h2>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setQuoteOpen(true)} data-testid="cta-quote-btn" className="h-14 px-8 bg-black text-white font-mono-a text-sm uppercase tracking-widest hover:bg-[#111] transition-colors">
              Get Free Quote
            </button>
            <a href={PHONE_HREF} data-testid="cta-call-btn" className="h-14 inline-flex items-center gap-2 px-8 border-2 border-black text-black font-mono-a text-sm uppercase tracking-widest hover:bg-black hover:text-white transition-colors">
              <Phone className="h-4 w-4" /> {PHONE}
            </a>
          </div>
        </div>
      </section>

      <BookingSection />
      <ContactSection />

      {/* FOOTER */}
      <footer className="relative z-10 bg-[#050505] border-t border-[#222] px-4 md:px-12 lg:px-24 py-14">
        <div className="mx-auto max-w-[1400px]">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 pb-10 border-b border-[#222]">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center bg-[#FF4D00]"><Truck className="h-5 w-5 text-black" /></span>
              <span className="font-display text-2xl uppercase tracking-tight">Mile High Junk Guys</span>
            </div>
            <a href={PHONE_HREF} className="font-display text-3xl md:text-4xl uppercase tracking-tight hover:text-[#FF4D00] transition-colors" data-testid="footer-phone">
              {PHONE}
            </a>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-8 text-[#666] font-mono-a text-[11px] tracking-widest uppercase">
            <p>© {new Date().getFullYear()} Mile High Junk Guys · Family Owned & Operated</p>
            <p>{SERVICE_AREAS.join(" · ")}</p>
          </div>
        </div>
      </footer>

      {/* STICKY CALL CTA */}
      <a
        href={PHONE_HREF} data-testid="sticky-call-cta"
        className="pulse-cta fixed bottom-5 right-5 z-50 flex items-center gap-2 h-14 px-6 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a text-sm uppercase tracking-widest rounded-full transition-colors shadow-lg"
      >
        <Phone className="h-5 w-5" />
        <span className="hidden sm:inline">Call Now</span>
      </a>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
